class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (event) => {
      const fallback = event.target.closest('[data-cart-checkout-fallback]');
      if (fallback) this.continueToCheckout(event, fallback);
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();

    this.checkoutObserver = new MutationObserver(() => this.scheduleCheckoutGuard());
    this.checkoutObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'hidden', 'style'],
    });
    this.scheduleCheckoutGuard();
  }

  disconnectedCallback() {
    this.checkoutObserver?.disconnect();
    clearTimeout(this.checkoutGuardTimer);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    // Mobile uses Shopify's full cart page so checkout never depends on a
    // third-party script preserving the drawer's dynamically rendered footer.
    if (this.usesMobileCartPage()) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.usesMobileCartPage()) {
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }

    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.scheduleCheckoutGuard();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (this.usesMobileCartPage()) {
      if (typeof updateEmberHeaderCartCount === 'function') {
        updateEmberHeaderCartCount(parsedState.item_count);
      }
      window.location.assign(window.routes?.cart_url || '/cart');
      return;
    }

    this.productId = parsedState.id;

    if (typeof updateEmberHeaderCartCount === 'function' && typeof getCartCountFromCartIconSection === 'function') {
      const itemCount = getCartCountFromCartIconSection(parsedState.sections?.['cart-icon-bubble']);
      updateEmberHeaderCartCount(itemCount);
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.syncEmptyState(parsedState.item_count);
    this.scheduleCheckoutGuard();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  scheduleCheckoutGuard() {
    clearTimeout(this.checkoutGuardTimer);
    this.checkoutGuardTimer = setTimeout(() => this.ensureCheckoutAvailable(), 350);
  }

  ensureCheckoutAvailable() {
    const fallback = this.querySelector('[data-cart-checkout-fallback]');
    if (!fallback) return;

    const cartHasItems = this.syncEmptyState();
    const ctas = fallback.closest('.cart__ctas--drawer');
    const checkoutCandidates = ctas
      ? Array.from(ctas.querySelectorAll('#CartDrawer-Checkout, [data-route-copy]'))
      : [];
    const hasUsableCheckout = checkoutCandidates.some((candidate) => {
      const style = window.getComputedStyle(candidate);
      const bounds = candidate.getBoundingClientRect();

      return (
        !candidate.disabled &&
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        style.opacity !== '0' &&
        bounds.width > 0 &&
        bounds.height > 0
      );
    });

    fallback.toggleAttribute('hidden', !cartHasItems || hasUsableCheckout);
  }

  syncEmptyState(itemCount) {
    const hasItemCount = itemCount !== undefined && itemCount !== null && Number.isFinite(Number(itemCount));
    const cartHasItems = hasItemCount ? Number(itemCount) > 0 : Boolean(this.querySelector('.cart-item'));

    this.classList.toggle('is-empty', !cartHasItems);
    this.querySelector('.drawer__inner')?.classList.toggle('is-empty', !cartHasItems);

    return cartHasItems;
  }

  continueToCheckout(event, fallback) {
    event.preventDefault();
    if (fallback.getAttribute('aria-disabled') === 'true') return;

    fallback.setAttribute('aria-disabled', 'true');
    fallback.textContent = 'Opening checkout...';
    window.location.assign('/checkout');
  }

  usesMobileCartPage() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
